import axios from 'axios';
import React, { useState } from 'react';
import { useAuth } from 'context/useAuth';
import Button from 'components/Button/Button';
import { useNavigate } from 'react-router-dom';
import FormRow from 'components/FormRow/FormRow';
import { errorAsync, successAsync } from 'functions/notification';
import useAxiosGet from 'functions/hooks/useAxiosGet';
import { AgentInterface } from 'types/AgentInterface';
import { trimInputValue } from 'functions/trimInputValue';
import { FormInput } from 'components/FormInput/FormInput';
import { FormLayout } from 'layouts/FormLayout/FormLayout';
import { useAppSettings } from 'context/appsettingsContext';
import CircularProgress from '@mui/material/CircularProgress';
import { REGEX, validateInput } from 'functions/validationUtils';
import FormInputSelect, { SelectOptionType } from 'components/FormInputSelect/FormInputSelect';
import { ulid } from 'ulid';

export default function AddAgent() {

  const navigate = useNavigate();
  const settings = useAppSettings();
  const { authorisedUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: roles, loading: loadingRoles } = useAxiosGet('Roles');
  const isRSARegion = settings.firebaseProjectId.toLowerCase().endsWith('rsa') || settings.firebaseProjectId.toLowerCase().endsWith('rsa-stg');
  const isUSARegion = settings.firebaseProjectId.toLowerCase().endsWith('usa') || settings.firebaseProjectId.toLowerCase().endsWith('usa-stg');

  const roleOptions = roles.map((role: any) => ({
    key: role.id,
    value: role.id,
    label: role.name,
  }));

  const [currentRole, setCurrentRole] = useState({
    value: '',
    label: '',
  });

  const [formData, setFormData] = useState<AgentInterface>({
    FirstName: '',
    LastName: '',
    Email: '',
    phoneCode: isRSARegion ? '27' : isUSARegion ? '1' : /*isUKRegion*/ '44',
    PhoneNumber: '',
    RoleName: '',
  });

  const [formErrors, setFormErrors] = useState({
    FirstName: [],
    LastName: [],
    Email: [],
    PhoneNumber: [],
    RoleName: [],
  });

  function handleOnChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    const inputValue = trimInputValue(id, value);

    if (id === 'PhoneNumber') {
      if (REGEX.Numbers.test(inputValue) || !inputValue) {
        setFormData((prevState) => ({ ...prevState, [id]: inputValue }));
      }
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [id]: inputValue,
      }));
    }
  }

  function handleInputValidation(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setFormErrors((prevState) => ({
      ...prevState,
      [id]: validateInput(id, value, isRSARegion, isUSARegion, false),
    }));
  }

  async function handleSubmit() {
    
    setIsSubmitting(true);

    try {

      const response = await axios.post<any, any>(settings.addUserUrl, {
        agent: true,
        firstName: formData.FirstName,
        lastName: formData.LastName,
        email: formData.Email,
        emailVerified: true,
        phoneCode: isRSARegion ? '27' : isUSARegion ? '1' : /*isUKRegion*/ '44',
        phoneNumber: formData.PhoneNumber,
        idNumber: ulid(),
        idNumberIsSouthAfrican: false,
        role: formData.RoleName || 'Technical-Agent',
      }, { headers: { Authorization: `Bearer ${authorisedUser.accessToken}` } });

      successAsync(response?.data?.message || "Submitted");
      
      navigate('/');

    } catch (error: any) {
        errorAsync(error.response?.data?.message || "Error");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleRoleSelection(role: SelectOptionType) {
    setCurrentRole((prevState) => ({
      ...prevState,
      ...role,
    }));

    setFormData((prevState) => ({
      ...prevState,
      RoleName: role.label,
    }));
  }

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <FormLayout>
      <FormLayout.Header>
        <FormLayout.HeaderBack onClose={handleGoBack} />
        <p>Add agent</p>
      </FormLayout.Header>
      <FormLayout.Main>
        <FormRow>
          <FormInput
            id='FirstName'
            label='Name'
            onChange={handleOnChange}
            value={formData.FirstName}
            onBlur={handleInputValidation}
            errorMessage={formErrors.FirstName}
          />
        </FormRow>
        <FormRow>
          <FormInput
            id='LastName'
            label='Surname'
            onChange={handleOnChange}
            value={formData.LastName}
            onBlur={handleInputValidation}
            errorMessage={formErrors.LastName}
          />
        </FormRow>
        <FormRow>
          <FormInput
            type='tel'
            id='PhoneNumber'
            label='Cellphone number'
            onChange={handleOnChange}
            value={formData.PhoneNumber}
            onBlur={handleInputValidation}
            errorMessage={formErrors.PhoneNumber}
            countryCode={isRSARegion ? '27' : isUSARegion ? '1' : /*isUKRegion*/ '44'}
          />
        </FormRow>
        <FormRow>
          <FormInput
            id='Email'
            label='Email address'
            onChange={handleOnChange}
            value={formData.Email}
            onBlur={handleInputValidation}
            errorMessage={formErrors.Email}
          />
        </FormRow>

        <FormRow>
          {loadingRoles && (
            <div style={{ textAlign: 'center' }}>
              <CircularProgress size='2rem' />
            </div>
          )}
          {!loadingRoles && roles.length > 0 && (
            <FormInputSelect
              label='Role'
              options={roleOptions}
              value={currentRole}
              handleSelection={handleRoleSelection}
            />
          )}
        </FormRow>
      </FormLayout.Main>
      <FormLayout.Footer>
        <Button
          label={isSubmitting ? 'Submitting' : 'Add agent'}
          fullWidth
          onClick={handleSubmit}
          disabled={
            !formData.FirstName ||
            !formData.LastName ||
            !formData.Email ||
            !formData.Email ||
            !currentRole.label ||
            isSubmitting
          }
        />
      </FormLayout.Footer>
    </FormLayout>
  );
}
